/**
 * W6 trajectory-plan model — the pure, JSX-free derivation the screen + its
 * tests share. The planner authors survey STATIONS (MD · inclination · azimuth);
 * the SERVER computes min-curvature TVD / northing / easting / DLS and returns
 * them (pApi `putTrajectory`). This module therefore never re-implements the
 * survey math — it orders the returned stations, classifies each into a
 * trajectory segment (vertical → build → lateral), and projects them into the
 * two view geometries the screen draws (vertical section + N-E plan).
 *
 * Every function is total (null-safe) and guards degenerate input — an empty
 * station set, a single station, all-null computed columns — so a half-entered
 * draft or a real-backend body that omits the computed columns never throws
 * (the Oxbo-Z prod white-screen class). Inputs come through `asArray` at the
 * ingestion boundary; callers must never trust the raw query body.
 */
import type { TrajectoryStationInput, TrajectoryStationOut } from "@api/types";
import { asArray } from "@lib/asArray";

/** Coerce a possibly-null numeric field to a finite number (0 when absent). */
function num(v: number | null | undefined): number {
  return v != null && Number.isFinite(v) ? v : 0;
}

/** A finite number or null (never NaN / Infinity) — for computed columns. */
function finiteOrNull(v: number | null | undefined): number | null {
  return v != null && Number.isFinite(v) ? v : null;
}

/** Trajectory segment class, driven by inclination (well-planning vernacular). */
export type TrajectorySegment = "vertical" | "build" | "lateral";

/** Inclination (°) at/above which a station is treated as horizontal (lateral). */
export const LATERAL_INC_DEG = 88;
/** Inclination (°) below which a station is still effectively vertical. */
export const VERTICAL_INC_DEG = 3;

/**
 * A station with its computed columns coerced to finite-or-null, its derived
 * segment class, and horizontal displacement (√(N²+E²)) — the vertical-section
 * X axis. `seq` is re-stamped 1..N in MD order so an out-of-order backend body
 * or an in-app reorder is honoured on the next derive.
 */
export interface DerivedStation {
  seq: number;
  md: number;
  inclination: number;
  azimuth: number;
  tvd: number | null;
  northing: number | null;
  easting: number | null;
  dls: number | null;
  segment: TrajectorySegment;
  /** Horizontal displacement from surface (m): √(N² + E²), null if N/E absent. */
  displacement: number | null;
}

/** Classify a station into a trajectory segment from its inclination (°). */
export function segmentFor(inclination: number | null | undefined): TrajectorySegment {
  const inc = num(inclination);
  if (inc >= LATERAL_INC_DEG) return "lateral";
  if (inc <= VERTICAL_INC_DEG) return "vertical";
  return "build";
}

/**
 * Order the stations by MD, re-stamp `seq` = position + 1, coerce the computed
 * columns to finite-or-null, and attach the derived segment + horizontal
 * displacement. `asArray` coerces a present-but-non-array body (`{}`, `null`, an
 * error envelope, `{stations:null}`) to `[]` so a real-backend shape never
 * `.map`-crashes the screen; ordering by MD (not JSON order) is load-bearing
 * because a backend without `ORDER BY md` can return rows in any order and both
 * views + the DLS-max KPI read them in depth order.
 */
export function deriveStations(rows: unknown): DerivedStation[] {
  const ordered = [...asArray<TrajectoryStationOut>(rows)].sort((a, b) => num(a.md) - num(b.md));
  return ordered.map((s, i) => {
    const northing = finiteOrNull(s.northing);
    const easting = finiteOrNull(s.easting);
    const displacement =
      northing != null || easting != null ? Math.hypot(num(northing), num(easting)) : null;
    return {
      seq: i + 1,
      md: num(s.md),
      inclination: num(s.inclination),
      azimuth: num(s.azimuth),
      tvd: finiteOrNull(s.tvd),
      northing,
      easting,
      dls: finiteOrNull(s.dls),
      segment: segmentFor(s.inclination),
      displacement,
    };
  });
}

/** Strip a derived/out station back to the editable input triple (MD/inc/azi). */
export function toInput(s: {
  seq: number;
  md: number;
  inclination: number;
  azimuth: number;
}): TrajectoryStationInput {
  return { seq: s.seq, md: s.md, inclination: s.inclination, azimuth: s.azimuth };
}

/** KPI summary shown in the chips — all null-safe over a degenerate station set. */
export interface TrajectoryTotals {
  stations: number;
  /** Final planned MD (m) = deepest station. 0 when empty. */
  finalMd: number;
  /** Target TVD (m) = deepest station's TVD; null if no computed TVD present. */
  targetTvd: number | null;
  /** KOP MD (m): first station whose inclination leaves vertical; null if none. */
  kopMd: number | null;
  /** Lateral displacement (m) = the deepest station's horizontal displacement. */
  lateralDisplacement: number | null;
  /** Max planned DLS (°/30m) across stations; null if no computed DLS present. */
  maxDls: number | null;
}

export function trajectoryTotals(rows: unknown): TrajectoryTotals {
  const st = deriveStations(rows);
  const last = st[st.length - 1];
  // KOP = first station that has left vertical (inclination > VERTICAL_INC_DEG).
  const kop = st.find((s) => s.inclination > VERTICAL_INC_DEG) ?? null;
  const dlsValues = st.map((s) => s.dls).filter((d): d is number => d != null);
  return {
    stations: st.length,
    finalMd: last ? last.md : 0,
    // The deepest station's TVD (not max TVD) — a lateral can rise slightly.
    targetTvd: last ? last.tvd : null,
    kopMd: kop ? kop.md : null,
    lateralDisplacement: last ? last.displacement : null,
    maxDls: dlsValues.length ? Math.max(...dlsValues) : null,
  };
}

/** DLS (°/30m) at/above which the curve is flagged high (well-planning heuristic). */
export const DLS_WARN = 6;

/**
 * A plotted vertex of the VERTICAL-SECTION view: TVD (down) vs. horizontal
 * displacement (right). Stations missing a computed TVD or displacement are
 * dropped (they cannot be placed), so a not-yet-computed draft plots what it can
 * rather than throwing. Always includes the surface origin (0,0) when at least
 * one placeable station exists.
 */
export interface SectionPoint {
  seq: number;
  segment: TrajectorySegment;
  /** Horizontal displacement (m) — the X axis. */
  x: number;
  /** True vertical depth (m) — the Y axis (grows downward). */
  y: number;
}

export function sectionPoints(rows: unknown): SectionPoint[] {
  const st = deriveStations(rows);
  const pts: SectionPoint[] = [];
  for (const s of st) {
    if (s.tvd == null || s.displacement == null) continue;
    pts.push({ seq: s.seq, segment: s.segment, x: s.displacement, y: s.tvd });
  }
  return pts;
}

/**
 * A plotted vertex of the PLAN (N-E) view: easting (X) vs. northing (Y, up).
 * Stations missing northing AND easting are dropped. A single surface station
 * (0,0) is a valid degenerate plot (a vertical well shows one dot at origin).
 */
export interface PlanPoint {
  seq: number;
  segment: TrajectorySegment;
  /** Easting (m) — the X axis. */
  e: number;
  /** Northing (m) — the Y axis (grows up = north). */
  n: number;
}

export function planPoints(rows: unknown): PlanPoint[] {
  const st = deriveStations(rows);
  const pts: PlanPoint[] = [];
  for (const s of st) {
    if (s.northing == null && s.easting == null) continue;
    pts.push({ seq: s.seq, segment: s.segment, e: num(s.easting), n: num(s.northing) });
  }
  return pts;
}

/** An empty station for the "+ add station" affordance (seq re-derived on commit). */
export function emptyStation(index: number): TrajectoryStationOut {
  return {
    seq: index + 1,
    md: 0,
    inclination: 0,
    azimuth: 0,
    tvd: null,
    northing: null,
    easting: null,
    dls: null,
  };
}
